import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { useAppTranslation } from '../../../services/i18n';

type Props = {
  label: string;
  /** Draws the red POVINNÉ chip beside the label. */
  required?: boolean;
  children: React.ReactNode;
};

/**
 * Label above the field, chip beside the label, value below.
 *
 * MUI's own label floats *inside* the box and shrinks to caption size once the
 * field has a value, which reads as a placeholder rather than as a name for
 * what is in the field. The design gives every field a proper label line, and
 * marks the required ones with a chip instead of an asterisk — so the fields
 * here are rendered without their MUI label and given this one.
 */
export function LabeledField({ label, required, children }: Props) {
  return (
    <Box sx={{ marginBottom: '20px' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
      >
        <Typography
          sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-black)' }}
        >
          {label}
        </Typography>
        {required && <RequiredChip />}
      </Box>
      {children}
    </Box>
  );
}

function RequiredChip() {
  const t = useAppTranslation();

  return (
    <Box
      sx={{
        // Palette tokens rather than the mock's literals, as everywhere else
        // in Klara: both are a shade off #fee2e2 / #dc2626 and read the same.
        backgroundColor: 'var(--color-error-back)',
        color: 'var(--color-error)',
        borderRadius: '4px',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        lineHeight: 1.4,
      }}
    >
      {t('klara.inputs.required')}
    </Box>
  );
}
